import React, { useId, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { duration } from './primitives/tokens';
import { useControllable } from '../../hooks/useControllable';
import { Icon } from './Icon';
import { slidingIndicator } from './primitives/motion/presets';
import styles from './Tabs.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const tabButton = cva(styles.tabButton, {
  variants: {
    size: {
      sm: styles.tabSm,
      md: styles.tabMd,
      lg: styles.tabLg,
    },
    active: { true: styles.tabButtonActive },
    disabled: { true: styles.tabButtonDisabled },
  },
  defaultVariants: {
    size: 'md',
  },
});

const addTabButton = cva(styles.addTabBtn, {
  variants: {
    size: {
      sm: styles.tabSm,
      md: styles.tabMd,
      lg: styles.tabLg,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type TabsType = 'line' | 'card' | 'editable-card';
type TabsSize = 'sm' | 'md' | 'lg';
type TabPosition = 'top' | 'bottom' | 'left' | 'right';

interface TabItem {
  key: string;
  label: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  closable?: boolean;
  /** Accessible name for the tab button when the visible label is icon-only. */
  ariaLabel?: string;
}

interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled active tab key */
  activeKey?: string;
  /** Default active tab */
  defaultActiveKey?: string;
  /** Tab items */
  items: TabItem[];
  /** Change handler */
  onChange?: (key: string) => void;
  /** Visual type */
  type?: TabsType;
  /** Size */
  size?: TabsSize;
  /** Tab position */
  tabPosition?: TabPosition;
  /** Center tabs */
  centered?: boolean;
  /** Called when a tab is added or removed (editable-card) */
  onEdit?: (key: string, action: 'add' | 'remove') => void;
  /** Custom add icon */
  addIcon?: React.ReactNode;
}


/**
 * Tabs — tabbed content panels.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | activeKey | string | — | Controlled active tab |
 * | defaultActiveKey | string | first item | Default active |
 * | items | TabItem[] | — | Tab definitions |
 * | onChange | (key) => void | — | Tab change handler |
 * | type | 'line' \| 'card' \| 'editable-card' | 'line' | Visual style |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Size |
 * | tabPosition | 'top' \| 'bottom' \| 'left' \| 'right' | 'top' | Position |
 * | centered | boolean | false | Center tabs |
 * | onEdit | (key, action) => void | — | Add/remove handler |
 * | addIcon | ReactNode | + icon | Custom add button |
 *
 * @example
 * <Tabs items={[
 *   { key: '1', label: 'Tab 1', children: <div>Content 1</div> },
 *   { key: '2', label: 'Tab 2', children: <div>Content 2</div> },
 * ]} />
 */
export function Tabs({
  activeKey: controlledKey,
  defaultActiveKey,
  items,
  onChange,
  type = 'line',
  size = 'md',
  tabPosition = 'top',
  centered = false,
  onEdit,
  addIcon,
  style,
  className,
  ...props
}: TabsProps) {
  const defKey = defaultActiveKey ?? items[0]?.key ?? '';
  const [activeKey, setActiveKey] = useControllable(controlledKey, defKey, onChange);
  const isVertical = tabPosition === 'left' || tabPosition === 'right';
  const indicatorId = useId();
  const reducedMotion = useReducedMotion();

  const activeContent = items.find((item) => item.key === activeKey)?.children;

  // Root flex direction is driven by tabPosition — runtime, passed as CSS custom property
  const rootFlexDirection = isVertical
    ? tabPosition === 'right'
      ? 'row-reverse'
      : 'row'
    : tabPosition === 'bottom'
      ? 'column-reverse'
      : 'column';

  const tabList = (
    <div
      role="tablist"
      className={clsx(styles.tabList, type === 'line' && styles.tabListLine)}
      style={{
        '--tab-list-direction': isVertical ? 'column' : 'row',
        '--tab-list-justify': centered ? 'center' : 'flex-start',
      } as React.CSSProperties}
    >
      {items.map((item) => (
        <TabButton
          key={item.key}
          item={item}
          active={activeKey === item.key}
          type={type}
          size={size}
          isVertical={isVertical}
          tabPosition={tabPosition}
          indicatorId={indicatorId}
          reducedMotion={!!reducedMotion}
          onClick={() => !item.disabled && setActiveKey(item.key)}
          onClose={type === 'editable-card' && item.closable !== false ? () => onEdit?.(item.key, 'remove') : undefined}
        />
      ))}
      {type === 'editable-card' && (
        <button
          onClick={() => onEdit?.('', 'add')}
          aria-label="Add tab"
          className={addTabButton({ size })}
        >
          {addIcon ?? <Icon name="add" size={14} />}
        </button>
      )}
    </div>
  );

  const content =
    activeContent !== undefined ? (
      <AnimatePresence mode="wait">
        <motion.div
          key={activeKey}
          role="tabpanel"
          id={`tabpanel-${activeKey}`}
          aria-labelledby={`tab-${activeKey}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: duration.normal / 1000 }}
          className={styles.tabPanel}
        >
          {activeContent}
        </motion.div>
      </AnimatePresence>
    ) : null;

  return (
    <div
      className={clsx(styles.root, className)}
      // flexDirection is runtime-computed from tabPosition — passed as CSS custom property
      style={{ '--tabs-flex-direction': rootFlexDirection, ...style } as React.CSSProperties}
      {...props}
    >
      {tabList}
      {content}
    </div>
  );
}

function TabButton({
  item,
  active,
  type: _type,
  size,
  isVertical: _isVertical,
  tabPosition: _tabPosition,
  indicatorId,
  reducedMotion,
  onClick,
  onClose,
}: {
  item: TabItem;
  active: boolean;
  type: TabsType;
  size: TabsSize;
  isVertical: boolean;
  tabPosition: TabPosition;
  indicatorId: string;
  reducedMotion: boolean;
  onClick: () => void;
  onClose?: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      id={`tab-${item.key}`}
      role="tab"
      aria-selected={active}
      aria-controls={`tabpanel-${item.key}`}
      aria-disabled={item.disabled}
      aria-label={item.ariaLabel}
      onClick={item.disabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={clsx(tabButton({ size, active, disabled: item.disabled }), _type === 'line' && styles.tabButtonLine)}
    >
      {active && _type === 'line' && (
        <motion.div
          className={styles.tabUnderline}
          layoutId={`tabs-indicator-${indicatorId}`}
          {...slidingIndicator}
          transition={reducedMotion ? { duration: 0 } : slidingIndicator.transition}
        />
      )}
      {active && _type !== 'line' && (
        <motion.div
          className={styles.tabIndicator}
          layoutId={`tabs-indicator-${indicatorId}`}
          {...slidingIndicator}
          transition={reducedMotion ? { duration: 0 } : slidingIndicator.transition}
        />
      )}
      {item.icon && <span className={styles.tabIcon}>{item.icon}</span>}
      <span>{item.label}</span>
      {onClose && (
        <button
          type="button"
          aria-label={`Close ${typeof item.label === 'string' ? item.label : 'tab'}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className={clsx(styles.tabCloseBtn, (hovered || active) && styles.tabCloseVisible)}
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </button>
  );
}
